import { useMemo, useState } from "react";
import { useLocation, Link } from "wouter";
import { motion } from "framer-motion";
import { Heart, SlidersHorizontal, Trash2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { VehicleSearchSelect } from "@/components/add-car/vehicle-search-select";
import { formatSiteNumber } from "@shared/currency";
import {
  BODY_TYPE_CATEGORIES,
  BODY_TYPE_IMAGES,
  BODY_TYPE_LABELS,
  BODY_TYPE_MIRROR,
  BODY_TYPE_WHITE_BG,
  type BodyTypeCategory,
} from "@/components/car-listing/body-types";
import { FUEL_FILTER_LABELS_EN, FUEL_TYPES } from "@shared/fuel-types";
import { homeHeroLayout } from "@/components/home/home-hero-layout";
import { buildListingsHref } from "@/lib/listings-search-params";

type SaleTypeFilter = "all" | "auction" | "fixed";

const SALE_TYPE_OPTIONS: { value: SaleTypeFilter; label: string }[] = [
  { value: "all", label: "الكل" },
  { value: "auction", label: "مزاد" },
  { value: "fixed", label: "سعر ثابت" },
];

const heroSelectTriggerClass =
  "h-11 w-full rounded-xl border-neutral-300 bg-white px-3 text-sm font-medium text-[#0f172a] shadow-none focus-visible:ring-1 focus-visible:ring-[#0071BC]/25 data-[size=default]:h-11 lg:h-12 lg:data-[size=default]:h-12";


function FilterField({
  label,
  fieldId,
  children,
}: {
  label: string;
  fieldId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}

export function HomeHeroSearch({ className }: { className?: string }) {
  const [, setLocation] = useLocation();
  const [saleType, setSaleType] = useState<SaleTypeFilter>("all");
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [modelId, setModelId] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText.trim(), 300);

  const { data: brands = [] } = trpc.cars.getBrands.useQuery();

  const brandIdsForModels = useMemo(
    () => brandIds.map(Number).filter((id) => Number.isInteger(id) && id > 0),
    [brandIds],
  );

  const { data: modelsForBrand = [], isFetching: modelsFetching } =
    trpc.cars.getModelsForBrands.useQuery(brandIdsForModels, {
      enabled: brandIdsForModels.length > 0,
    });

  const brandOptions = useMemo(
    () =>
      brands.map((b) => ({
        value: String(b.id),
        nameAr: b.nameAr,
        nameEn: b.nameEn,
        logo: b.logo,
      })),
    [brands],
  );

  const modelOptions = useMemo(
    () =>
      modelsForBrand.map((m) => ({
        value: String(m.id),
        nameAr: m.nameAr,
        nameEn: m.nameEn,
      })),
    [modelsForBrand],
  );

  const bodyOptions = useMemo(
    () =>
      BODY_TYPE_CATEGORIES.map((cat) => {
        const labels = BODY_TYPE_LABELS[cat as BodyTypeCategory];
        return {
          value: cat,
          nameAr: labels.ar,
          nameEn: labels.en,
          image: BODY_TYPE_IMAGES[cat],
          imageWhiteBg: BODY_TYPE_WHITE_BG[cat] === true,
          imageMirror: BODY_TYPE_MIRROR[cat] === true,
        };
      }),
    [],
  );

  const fuelOptions = useMemo(
    () =>
      FUEL_TYPES.map((fuel) => ({
        value: fuel,
        nameAr: fuel,
        nameEn: FUEL_FILTER_LABELS_EN[fuel],
      })),
    [],
  );

  const countInput = useMemo(
    () => ({
      ...(saleType === "auction" || saleType === "fixed" ? { saleType } : {}),
      ...(brandIdsForModels.length ? { brandIds: brandIdsForModels } : {}),
      ...(modelId ? { modelId: Number(modelId) } : {}),
      ...(bodyType ? { bodyTypes: [bodyType] } : {}),
      ...(fuelType ? { fuelTypes: [fuelType] } : {}),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [saleType, brandIdsForModels, modelId, bodyType, fuelType, debouncedSearch]
  );

  const { data: resultCount } = trpc.cars.count.useQuery(countInput, {
    staleTime: 30_000,
  });

  const handleBrandChange = (next: string[]) => {
    setBrandIds(next);
    setModelId("");
  };

  const resetFilters = () => {
    setSaleType("all");
    setBrandIds([]);
    setModelId("");
    setBodyType("");
    setFuelType("");
    setSearchText("");
  };

  const handleSearch = () => {
    setLocation(
      buildListingsHref({
        saleType,
        brandIds,
        modelId,
        bodyType,
        fuelType,
        search: searchText,
      })
    );
  };

  const countLabel =
    typeof resultCount === "number" ? formatSiteNumber(resultCount) : "…";

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={cn("relative z-20 w-full", className)}
      dir="rtl"
    >
      <div className={homeHeroLayout.searchCard}>
        <p className="mb-3 text-[0.8rem] leading-relaxed text-neutral-500 sm:mb-4 sm:text-sm">
          يمكنك البحث بالماركة، الموديل، المحرك، أو التجهيزات
        </p>

        {/* Free-text search */}
        <div className="mb-4 lg:mb-5">
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
            placeholder="ابحث عن أي سيارة هنا..."
            className="h-12 w-full rounded-xl border border-neutral-300 bg-white px-3.5 text-sm text-[#0f172a] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#0071BC] focus:ring-1 focus:ring-[#0071BC]/25 lg:h-[3.25rem] lg:text-[0.95rem]"
            aria-label="بحث عن سيارة"
          />
        </div>

        {/* Sale type · mobile only (desktop matches 4-filter reference) */}
        <div
          className="mb-4 flex gap-1.5 overflow-x-auto pb-0.5 lg:hidden"
          role="group"
          aria-label="نوع العرض"
        >
          {SALE_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSaleType(opt.value)}
              className={cn(
                "shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors",
                saleType === opt.value
                  ? "bg-[#0f172a] text-white"
                  : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200/80"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filters: stacked mobile · 4-col desktop row */}
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
          <FilterField label="الماركة" fieldId="hero-brand">
            <VehicleSearchSelect
              id="hero-brand"
              variant="make"
              multiple
              values={brandIds}
              onValuesChange={handleBrandChange}
              options={brandOptions}
              placeholder="اختر الماركة"
              searchPlaceholder="ابحث عن ماركة…"
              emptyText="لا توجد ماركات مطابقة"
              noneOption={{ nameAr: "جميع الماركات", nameEn: "All makes" }}
              triggerClassName={heroSelectTriggerClass}
            />
          </FilterField>

          <FilterField label="الموديل" fieldId="hero-model">
            <VehicleSearchSelect
              id="hero-model"
              variant="model"
              value={modelId || undefined}
              onValueChange={setModelId}
              disabled={brandIds.length === 0}
              loading={brandIds.length > 0 && modelsFetching}
              options={modelOptions}
              placeholder={brandIds.length > 0 ? "اختر الموديل" : "اختر الماركة أولاً"}
              searchPlaceholder="ابحث عن موديل…"
              emptyText="لا توجد موديلات مطابقة"
              noneOption={brandIds.length > 0 ? { nameAr: "أي موديل", nameEn: "Any model" } : undefined}
              triggerClassName={cn(heroSelectTriggerClass, "disabled:opacity-55")}
            />
          </FilterField>

          <FilterField label="نوع الهيكل · Body type" fieldId="hero-body">
            <VehicleSearchSelect
              id="hero-body"
              variant="body"
              value={bodyType || undefined}
              onValueChange={setBodyType}
              options={bodyOptions}
              placeholder="اختر نوع الهيكل"
              searchPlaceholder="ابحث عن هيكل…"
              emptyText="لا توجد أنواع مطابقة"
              noneOption={{ nameAr: "أي هيكل", nameEn: "Any body" }}
              triggerClassName={heroSelectTriggerClass}
            />
          </FilterField>

          <FilterField label="الوقود" fieldId="hero-fuel">
            <VehicleSearchSelect
              id="hero-fuel"
              variant="model"
              value={fuelType || undefined}
              onValueChange={setFuelType}
              options={fuelOptions}
              placeholder="اختر نوع الوقود"
              searchPlaceholder="ابحث عن وقود…"
              emptyText="لا توجد أنواع مطابقة"
              noneOption={{ nameAr: "أي وقود", nameEn: "Any fuel" }}
              triggerClassName={heroSelectTriggerClass}
            />
          </FilterField>
        </div>

        {/* Footer · search, favorites, and clear on the visual right */}
        <div className="mt-5 flex flex-col gap-3 border-t border-neutral-100 pt-5 sm:mt-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <div className="flex w-full flex-col gap-2.5 sm:flex-row sm:items-center lg:w-auto">
            <button
              type="button"
              onClick={handleSearch}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#0f172a] px-7 text-sm font-semibold text-white transition-all hover:bg-black active:scale-[0.98] sm:w-auto lg:h-12 lg:px-8"
            >
              ابحث عن سيارة ({countLabel})
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full border border-red-600 bg-transparent px-5 text-sm font-medium text-red-600 transition-colors hover:bg-transparent hover:text-red-700 sm:w-auto lg:h-12"
            >
              <Trash2 className="size-4 shrink-0" strokeWidth={1.75} aria-hidden />
              مسح الفلتر
            </button>
            <Link
              href="/dashboard/bidder/favorites"
              className="inline-flex min-h-11 items-center justify-center gap-2 text-sm font-medium text-neutral-700 transition-colors hover:text-[#0071BC] sm:px-2"
            >
              <Heart className="size-4 shrink-0" aria-hidden />
              مفضلتي
            </Link>
          </div>

          <Link
            href="/listings"
            className="inline-flex min-h-11 items-center justify-center gap-2 text-sm font-medium text-neutral-700 transition-colors hover:text-[#0071BC] lg:justify-start"
          >
            <SlidersHorizontal className="size-4 shrink-0" aria-hidden />
            فتح المزيد من الفلاتر
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
